import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePostApiAgents } from "@/lib/api/generated/agents/agents";
import { ControlApiError } from "@/lib/api/client";
import type { AgentRuntimePolicy } from "../../../../../packages/shared-types/src/index.js";
import {
  DEFAULT_AGENT_MEMORY_POLICY,
  DEFAULT_AGENT_TOOL_ALLOWLIST,
} from "../../../../../packages/shared-types/src/index.js";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import {
  AGENT_IDENTITY_PROMPT_KEY,
  AGENT_INSTRUCTIONS_PROMPT_KEY,
  AGENT_SOUL_PROMPT_KEY,
  AGENT_USER_PROMPT_KEY,
  promptDocumentWrite,
} from "@/lib/types/control-plane-agents";

const executionClasses = [
  "chat_only",
  "approved_actions",
  "operator_maintenance",
] as const;

const agentStatuses = ["draft", "active", "paused"] as const;

const createAgentSchema = z.object({
  agentId: z
    .string()
    .min(1, "Agent ID is required")
    .regex(/^[a-z0-9_-]+$/, "Lowercase alphanumeric, hyphens, underscores only"),
  name: z.string().min(1, "Name is required"),
  slug: z.string().optional(),
  description: z.string().optional(),
  status: z.enum(agentStatuses).default("draft"),
  soulMarkdown: z.string().min(1, "SOUL.md is required"),
  agentsMarkdown: z.string().min(1, "AGENTS.md is required"),
  identityMarkdown: z.string().optional(),
  userMarkdown: z.string().optional(),
  executionClass: z.enum(executionClasses).default("approved_actions"),
});

type CreateAgentForm = z.infer<typeof createAgentSchema>;

function buildDefaultPolicy(executionClass: CreateAgentForm["executionClass"]): AgentRuntimePolicy {
  return {
    executionClass,
    enabledWorkflowKeys: [],
    enabledConnectorKeys: [],
    allowedToolNames: [...DEFAULT_AGENT_TOOL_ALLOWLIST],
    runtimeConnectorAccess: {
      allowedBindingIds: [],
      allowedTargetKeysByBindingId: {},
      defaultTargetKeyByBindingId: {},
    },
    dangerousToolDeny: ["exec", "bash", "process"],
    allowCrossConversationSummaryAccess: false,
    memoryPolicy: { ...DEFAULT_AGENT_MEMORY_POLICY },
    responseStyle: {},
  };
}

export function CreateAgentPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const mutation = usePostApiAgents();
  useDocumentTitle("New Agent");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateAgentForm>({
    resolver: zodResolver(createAgentSchema),
    defaultValues: {
      status: "draft",
      executionClass: "approved_actions",
    },
  });

  const onSubmit = async (data: CreateAgentForm) => {
    try {
      const promptDocuments = [
        promptDocumentWrite(AGENT_SOUL_PROMPT_KEY, data.soulMarkdown),
        promptDocumentWrite(AGENT_INSTRUCTIONS_PROMPT_KEY, data.agentsMarkdown),
      ];
      if (data.identityMarkdown?.trim()) {
        promptDocuments.push(promptDocumentWrite(AGENT_IDENTITY_PROMPT_KEY, data.identityMarkdown));
      }
      if (data.userMarkdown?.trim()) {
        promptDocuments.push(promptDocumentWrite(AGENT_USER_PROMPT_KEY, data.userMarkdown));
      }

      await mutation.mutateAsync({
        data: {
          tenantId: tenantId!,
          agentId: data.agentId,
          name: data.name,
          slug: data.slug,
          status: data.status,
          description: data.description,
          promptDocuments,
          runtimePolicy: buildDefaultPolicy(data.executionClass),
        },
      });
      toast.success(`Agent "${data.name}" created`);
      navigate(`/workspace/agents/${data.agentId}`);
    } catch (err) {
      if (err instanceof ControlApiError) {
        const body = err.body as Record<string, unknown> | null;
        toast.error(
          typeof body?.message === "string" ? body.message : err.message,
        );
      } else {
        toast.error("Failed to create agent");
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Create Agent
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>Agent Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Agent ID"
                error={errors.agentId?.message}
                helpText="Lowercase letters, numbers, hyphens, and underscores. This cannot be changed later."
              >
                <input
                  {...register("agentId")}
                  placeholder="agent_sales"
                  className="form-input"
                />
              </FormField>
              <FormField label="Name" error={errors.name?.message}>
                <input
                  {...register("name")}
                  placeholder="Sales Assistant"
                  className="form-input"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Slug" error={errors.slug?.message}>
                <input {...register("slug")} placeholder="sales" className="form-input" />
              </FormField>
              <FormField label="Status">
                <select {...register("status")} className="form-input">
                  {agentStatuses.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>

            <FormField label="Description" error={errors.description?.message}>
              <input
                {...register("description")}
                placeholder="A brief description of the agent"
                className="form-input"
              />
            </FormField>

            <FormField
              label="Execution Class"
              helpText="chat_only: no workflow access. approved_actions: workflows require approval. operator_maintenance: full access."
            >
              <select {...register("executionClass")} className="form-input">
                {executionClasses.map((ec) => (
                  <option key={ec} value={ec}>
                    {ec.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="SOUL.md" error={errors.soulMarkdown?.message}>
              <textarea
                {...register("soulMarkdown")}
                rows={6}
                className="w-full rounded-[var(--radius-sm)] border border-border p-3 text-xs font-mono resize-y"
                style={{ background: "var(--bg-elevated)", color: "var(--text)" }}
                placeholder="Describe the agent identity and persona in markdown..."
              />
            </FormField>

            <FormField label="AGENTS.md" error={errors.agentsMarkdown?.message}>
              <textarea
                {...register("agentsMarkdown")}
                rows={6}
                className="w-full rounded-[var(--radius-sm)] border border-border p-3 text-xs font-mono resize-y"
                style={{ background: "var(--bg-elevated)", color: "var(--text)" }}
                placeholder="Write agent instructions in markdown..."
              />
            </FormField>

            <FormField label="IDENTITY.md" error={errors.identityMarkdown?.message}>
              <textarea
                {...register("identityMarkdown")}
                rows={6}
                className="w-full rounded-[var(--radius-sm)] border border-border p-3 text-xs font-mono resize-y"
                style={{ background: "var(--bg-elevated)", color: "var(--text)" }}
                placeholder="Write agent identity markdown..."
              />
            </FormField>

            <FormField label="USER.md" error={errors.userMarkdown?.message}>
              <textarea
                {...register("userMarkdown")}
                rows={6}
                className="w-full rounded-[var(--radius-sm)] border border-border p-3 text-xs font-mono resize-y"
                style={{ background: "var(--bg-elevated)", color: "var(--text)" }}
                placeholder="Write user profile markdown..."
              />
            </FormField>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Agent"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/workspace/agents")}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({
  label,
  error,
  helpText,
  children,
}: {
  label: string;
  error?: string;
  helpText?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {helpText && !error && (
        <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          {helpText}
        </span>
      )}
      {error && (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      )}
    </div>
  );
}
